import { baseApi, INVOICE_LIST_TAGS } from "@/services/api/baseApi";
import { getBaseUrl } from "@/services/api/baseUrl";

/** Comment counts appear on list rows, so any comment change refreshes the lists too. */
const commentTags = (invoiceGuid) => [
  { type: "Comments", id: invoiceGuid ?? "LIST" },
  { type: "Comments", id: "LIST" },
  ...INVOICE_LIST_TAGS,
];

export const commentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getComments: builder.query({
      query: (invoiceGuid) => `/invoices/${invoiceGuid}/comments`,
      // Dates stay ISO here; the comment list formats them at render time.
      transformResponse: (response) => response ?? [],
      providesTags: (result, error, arg) => [
        { type: "Comments", id: arg },
        { type: "Comments", id: "LIST" },
      ],
    }),

    addComment: builder.mutation({
      query: ({ invoiceGuid, comment }) => ({
        url: `/invoices/${invoiceGuid}/comments`,
        method: "POST",
        body: { comment },
      }),
      invalidatesTags: (result, error, arg) => commentTags(arg.invoiceGuid),
    }),

    updateComment: builder.mutation({
      query: ({ guid, comment }) => ({
        url: `/invoices/comments/${guid}`,
        method: "PUT",
        body: { comment },
      }),
      invalidatesTags: (result, error, arg) => commentTags(arg.invoiceGuid),
    }),

    deleteComment: builder.mutation({
      query: ({ guid }) => ({
        url: `/invoices/comments/${guid}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => commentTags(arg.invoiceGuid),
    }),

    /**
     * Stores a file and returns `{ guid }`; the comment editor then links to
     * `/v1/attachments/{guid}`. Nothing is cached — the response is a one-shot
     * identifier, not a resource this app lists.
     *
     * `body` is a FormData, so no Content-Type is set: the browser has to add
     * its own multipart boundary. The old app used a raw axios call here with
     * the header hardcoded, which meant it did not share the token refresh.
     */
    uploadAttachment: builder.mutation({
      query: (file) => {
        const body = new FormData();
        body.append("file", file);
        return { url: "attachments", method: "POST", body };
      },
    }),
  }),
});

/** Where a stored attachment is served from, given the guid the upload returned. */
export function getAttachmentUrl(guid) {
  return `${getBaseUrl()}attachments/${guid}`;
}

export const {
  useGetCommentsQuery,
  useAddCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useUploadAttachmentMutation,
} = commentsApi;
